import { memo, useRef, useMemo, useCallback, useState, useEffect, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';

import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors, getSelectsOptions } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, RenderFormItem, SelectsOptions, ValidationError } from '@ohpen/enso-ui';
import {
  ProcurationFeeRuleResponse,
  UsageBatchResponse,
  DraftProcurationFeeRule,
  ChangeRequestOfDraftProcurationFeeRule,
  ProcurationFeeRulesCrudChangeResponse,
  procurationFeeRulesCheckUsage,
  procurationFeeRulesCreateChangeRequest,
} from '@pmp/api';
import { crudToActionMap, getApiErrorMessage, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { CrudFormProps } from '@pmp/types';
import { useFeatureToggle } from '@pmp/hooks';

import { getFeeRulesFormSchema } from '../formConfig';

export const ProcurationFeeRulesCrudForm = memo(
  ({ mode, record, valueLists, refreshTable, onClose }: CrudFormProps<ProcurationFeeRuleResponse>): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(false);
    const [isUsed, setIsUsed] = useState<boolean | null>(null);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    useEffect(() => {
      if (isUsed !== null) return;
      if (mode !== CrudMode.EDIT) {
        setIsUsed(false);
        setOpen(true);
        return;
      }
      const { id } = record || {};

      if (!id) {
        notification().error({ title: t('global.errors.unexpected') });
        onClose();
        return;
      }
      procurationFeeRulesCheckUsage({ body: [id] })
        .then(({ data: { items } }: { data: UsageBatchResponse }) => {
          const isUsed = items?.some(item => item.isUsed === true) ?? false;
          setIsUsed(isUsed);
          setOpen(true);
        })
        .catch(error => {
          notification().error({ title: getApiErrorMessage(error) });
          onClose();
        });
    }, [mode, record, isUsed, t, onClose]);

    const onConfirm: (formData: DraftProcurationFeeRule) => Promise<ValidationError[] | void> = useCallback(
      data => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        const body: ChangeRequestOfDraftProcurationFeeRule = {
          data,
          actionType: crudToActionMap[mode],
        };
        return procurationFeeRulesCreateChangeRequest({ body }).then(
          ({ data: { isValid, errors } }: { data: ProcurationFeeRulesCrudChangeResponse }) => {
            if (!isValid) {
              const form = ref.current?.form;
              if (form) {
                const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
                if (errorFields.length) form.setFields(errorFields);
              }

              return transformCrudFormErrors(errors);
            }
            if (mode === CrudMode.EDIT || !enableFourEyes) refreshTable();
            notification().success({
              title: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [mode, refreshTable, t, enableFourEyes],
    );

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const renderAlert: RenderFormItem<DraftProcurationFeeRule> = useCallback(
      (_schema, _config, _form) => {
        if (mode === CrudMode.EDIT && isUsed)
          return <Alert message={t('lender.procurationFees.labels.procurationFeeRuleInUseMessage')} type="warning" showIcon />;
        return null;
      },
      [mode, isUsed, t],
    );

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('lender.procurationFees.labels.procurationFeeRule') }),
        [CrudMode.CREATE]: t('enso.labels.createX', { x: t('lender.procurationFees.labels.procurationFeeRule') }),
        [CrudMode.EDIT]: t('enso.labels.editX', { x: t('lender.procurationFees.labels.procurationFeeRule') }),
      };
      return titles[mode];
    }, [mode, t]);

    const initialValues = useMemo<DraftProcurationFeeRule | null>(() => {
      if (!record) return null;
      return record as DraftProcurationFeeRule;
    }, [record]);

    const crudProps: CrudProps<DraftProcurationFeeRule> = useMemo(() => {
      const selectsOptions: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};

      return {
        ref,
        open,
        schema: getFeeRulesFormSchema(selectsOptions, renderAlert, mode),
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('enso.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        initialValues,
        mode,
        readonly: mode === CrudMode.VIEW,
        width: 'm',
        onClose: handleOnClose,
        onConfirm,
      };
    }, [valueLists, open, renderAlert, mode, title, t, initialValues, handleOnClose, onConfirm, enableFourEyes]);

    if (isUsed === null) return null;

    return <Crud {...crudProps} />;
  },
);
